import { api, readTotalCount } from '../api/client';
import { API_COLLECTIONS_URL, API_URL } from '../utils';
import { normalizeId } from '../utils/ids';

// Publishing (keepr-api KPR-1331, docs/SCHEMA.md "Publishing: a publisher's
// own packages"): a third-party publisher's own accounts make, change,
// publish, rebuild and retire that publisher's packages, and delete one that
// was never listed (KPR-1445, at most 20 of which a publisher holds). Every version that
// would change what Discover lists waits for a reviewer: the API answers 202
// `{ held }` for it, and `isHeld` says so. Every write sends the `rev` the
// page read, so a page another account changed since is refused (409
// `package_changed`) rather than written over. Errors propagate: every caller
// words its own refusal (`publishingRefusal`).

export const API_PUBLISHING_PACKAGES_URL = `${API_URL}/publishing/packages`;

/** What a version held for review says, wherever it is sent from. */
export const HELD_WORDS = 'Sent for review. It is listed once a reviewer approves it.';

/** Did the API hold this version for a reviewer (202 `{ held }`)? */
export const isHeld = answer => Boolean(answer && answer.held && answer.held.state === 'held');

const revQuery = rev => (Number.isInteger(rev) ? { params: { rev } } : {});

/**
 * How many never-listed packages the publisher holds and the most it may
 * (keepr-api KPR-1445: `X-Draft-Count`, `X-Draft-Limit`), or null when the
 * API did not say (an older API): nothing is then said before a create.
 */
export function readDraftAllowance(headers) {
	// Found whatever its case, as readTotalCount finds X-Total-Count.
	const read = (name) => {
		if (!headers) return null;
		const key = name in headers ? name : Object.keys(headers).find(k => k.toLowerCase() === name);
		const raw = key === undefined ? undefined : headers[key];
		if (raw === undefined || raw === null || raw === '') return null;
		const n = Number(raw);
		return Number.isInteger(n) && n >= 0 ? n : null;
	};
	const count = read('x-draft-count');
	const limit = read('x-draft-limit');
	return count === null || limit === null ? null : { count, limit };
}

/** Has the publisher as many never-listed packages as it may hold? */
export const atDraftLimit = drafts => Boolean(drafts && drafts.limit > 0 && drafts.count >= drafts.limit);

/** The one line that says the limit, before a create and when one is refused. */
export const draftLimitWords = limit => `Your publisher holds ${limit} packages that were never listed, the most it can. Delete a draft you no longer need, or publish one, to make another.`;

/** `{ rows, total, drafts }` — the publisher's packages, every status, and its draft allowance. `q` is KQL over the packages subject. */
export async function listPublisherPackages({ q, skip = 0, limit = 200 } = {}) {
	const params = { skip, limit };
	if (q) params.q = q;
	const { data, headers } = await api.get(API_PUBLISHING_PACKAGES_URL, { params });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length), drafts: readDraftAllowance(headers) };
}

/** One of the publisher's packages, as its page shows it, with `rev`, `review`, `build` and `report`. */
export async function getPublisherPackage(packageId) {
	const { data } = await api.get(`${API_PUBLISHING_PACKAGES_URL}/${encodeURIComponent(packageId)}`);
	return data;
}

/** A draft from a collection the person manages: `{ key, kind, name?, category?, cardIds?, includeItems?, itemsRole? }` → the row. */
export async function createPublisherPackage(collectionId, body) {
	const { data } = await api.post(`${API_PUBLISHING_PACKAGES_URL}/from-collection/${encodeURIComponent(collectionId)}`, body);
	return data;
}

/**
 * The cards a collection owns — what a card or a bundle package can carry
 * (the build refuses any other) — as `[{ _id, name, icon, color }]`.
 */
export async function packageableCards(collectionId) {
	const { data } = await api.get(`${API_COLLECTIONS_URL}/${encodeURIComponent(collectionId)}/cards`);
	const id = String(collectionId);
	return (Array.isArray(data) ? data : [])
		.filter(card => card && card.scope === 'collection' && normalizeId(card.collection_id) === id)
		.map(card => ({ _id: normalizeId(card._id), name: card.name, icon: card.icon || '', color: card.color || '' }));
}

/** The listing → the row (a draft), or `{ held }` (a package ever listed). */
export async function patchPublisherPackage(packageId, body, rev) {
	const { data } = await api.patch(`${API_PUBLISHING_PACKAGES_URL}/${encodeURIComponent(packageId)}`, body, revQuery(rev));
	return data;
}

const lifecycle = verb => async (packageId, rev, body) => {
	const url = `${API_PUBLISHING_PACKAGES_URL}/${encodeURIComponent(packageId)}/${verb}`;
	const { data } = await api.post(url, body, revQuery(rev));
	return data;
};
/** → `{ held }` (sent for review), or the row for an admin's. */
export const publishPublisherPackage = lifecycle('publish');
/** → the row (a draft's new version), or `{ held }` (a package ever listed). */
export const rebuildPublisherPackage = lifecycle('rebuild');
/** → the row, retired. Never held. */
export const retirePublisherPackage = lifecycle('retire');

/**
 * A package that was never listed (the row's `canDelete`), deleted for good
 * with the page's `rev` (keepr-api KPR-1445) → `{ _id, key, deleted: true }`.
 * One ever listed is refused (409 `package_ever_listed`): retire it instead.
 */
export async function deletePublisherPackage(packageId, rev) {
	const { data } = await api.delete(`${API_PUBLISHING_PACKAGES_URL}/${encodeURIComponent(packageId)}`, revQuery(rev));
	return data;
}

/**
 * `{ rows, total }` — a package's versions, newest first (keepr-api KPR-1327):
 * `{ _id, change, version, state, submittedAt, decidedAt, reason, why, madeBy,
 * mine, canWithdraw, listed }`. Never who reviewed it.
 */
export async function listPackageVersions(packageId, { skip = 0, limit = 50 } = {}) {
	const { data, headers } = await api.get(`${API_PUBLISHING_PACKAGES_URL}/${encodeURIComponent(packageId)}/versions`, { params: { skip, limit } });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

/** The maker takes back a version still waiting → the version, withdrawn. */
export async function withdrawPackageVersion(packageId, versionId) {
	const { data } = await api.post(`${API_PUBLISHING_PACKAGES_URL}/${encodeURIComponent(packageId)}/versions/${encodeURIComponent(versionId)}/withdraw`);
	return data;
}

/**
 * A refusal at this door in a sentence a publisher can act on, or null when
 * the caller's own fallback says it better. The API's own words for the rest
 * (a build's refusal names what to change).
 */
export function publishingRefusal(error) {
	const data = error?.response?.data || {};
	switch (data.code) {
	case 'not_with_publisher': return data.message || 'Publishing is for the accounts a publisher names. Ask keepr to add your account to your publisher.';
	case 'package_changed': return 'This package changed since you opened it. It has been read again: make your change once more.';
	case 'package_published': return 'This package is listed already.';
	case 'package_key_taken': return 'Your publisher already has a package with that key. Choose another.';
	case 'category_not_listed': return 'Choose one of Discover\'s categories.';
	case 'publisher_admin_only': return 'Who publishes a package is set by keepr.';
	case 'not_your_version': return 'Only the account that sent this version can withdraw it.';
	case 'review_closed': return 'This version is no longer waiting for review.';
	case 'draft_limit_reached': return draftLimitWords(Number.isInteger(data.limit) ? data.limit : 20);
	case 'package_ever_listed': return 'This package was listed on Discover, so collections may have added it. Retire it instead.';
	default: return null;
	}
}
